export const CARD_PX = 248
export const HOUR_PX = CARD_PX + 48
export const DEFAULT_START_HOUR = 8
export const DEFAULT_END_HOUR = 19

export function minutesOf(time) {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return null
  const [hour, minute] = time.split(':').map(Number)
  if (hour > 23 || minute > 59) return null
  return hour * 60 + minute
}

export function hourRangeFor(dayIsos, slots) {
  const visible = new Set(dayIsos)
  let start = DEFAULT_START_HOUR
  let end = DEFAULT_END_HOUR

  for (const slot of slots || []) {
    if (!visible.has(slot?.scheduledLocal?.date)) continue
    const minutes = minutesOf(slot.scheduledLocal?.time)
    if (minutes == null) continue
    const hour = Math.floor(minutes / 60)
    const spillsIntoNextHour = minutes % 60 > 0
    start = Math.min(start, Math.max(0, hour))
    end = Math.max(end, Math.min(23, hour + (spillsIntoNextHour ? 1 : 0)))
  }

  return { start, end }
}

// Earliest post time among the shown days, used to scroll the grid to it.
export function earliestVisibleTime(dayIsos, slots) {
  const visible = new Set(dayIsos)
  let earliest = null
  for (const slot of slots || []) {
    if (!visible.has(slot?.scheduledLocal?.date)) continue
    const time = slot.scheduledLocal?.time
    if (minutesOf(time) == null) continue
    if (earliest == null || time < earliest) earliest = time
  }
  return earliest
}

export function hoursBetween(start, end) {
  const list = []
  for (let hour = start; hour <= end; hour += 1) list.push(hour)
  return list
}

const CARD_GAP = 8
const CARD_INSET = 2

function hourOf(time) {
  const minutes = minutesOf(time)
  return minutes == null ? null : Math.floor(minutes / 60)
}

// Offset inside an hour row on the base scale, so a lone 12:30 post sits halfway.
function minuteOffset(time) {
  const minutes = minutesOf(time)
  return minutes == null ? CARD_INSET : ((minutes % 60) / 60) * HOUR_PX + CARD_INSET
}

// Rows are HOUR_PX tall by default. Posts that start in the same hour on the
// same day stack one under another at full width, and the row grows to fit the
// busiest day, so 2, 3 or 4 posts at one time stay readable.
export function buildWeekLayout(dayIsos, slots, range) {
  const visible = new Set(dayIsos)
  const buckets = {}
  for (const slot of slots || []) {
    const date = slot?.scheduledLocal?.date
    if (!visible.has(date)) continue
    const hour = hourOf(slot.scheduledLocal?.time) ?? range.start
    ;(buckets[`${date}|${hour}`] ||= []).push(slot)
  }

  const heights = {}
  const offsets = new Map()
  for (const [key, bucket] of Object.entries(buckets)) {
    const hour = Number(key.split('|')[1])
    bucket.sort((a, b) =>
      (a.scheduledLocal?.time || '').localeCompare(b.scheduledLocal?.time || '')
    )
    let bottom = -Infinity
    for (const slot of bucket) {
      const top = Math.max(minuteOffset(slot.scheduledLocal?.time), bottom + CARD_GAP)
      offsets.set(slot, { hour, top })
      bottom = top + CARD_PX
    }
    heights[hour] = Math.max(heights[hour] ?? HOUR_PX, bottom + CARD_GAP)
  }

  const rows = []
  let cursor = 0
  for (const hour of hoursBetween(range.start, range.end)) {
    const height = heights[hour] ?? HOUR_PX
    rows.push({ hour, top: cursor, height })
    cursor += height
  }
  const rowTop = Object.fromEntries(rows.map((row) => [row.hour, row.top]))

  return {
    rows,
    height: cursor,
    slotTop(slot) {
      const placed = offsets.get(slot)
      return placed ? rowTop[placed.hour] + placed.top : CARD_INSET
    },
    timeTop(time) {
      const hour = hourOf(time) ?? range.start
      return (rowTop[hour] ?? 0) + minuteOffset(time)
    },
    hourAt(y) {
      const row = rows.find((item) => y < item.top + item.height) ?? rows[rows.length - 1]
      return row ? row.hour : range.start
    },
  }
}
